/**
 * DeepZoneWeightingPage — module placement weighted toward deepest 30%
 *
 * Imteaz & Asaeda 2000: modules placed in the deepest 30 % of footprint
 * deliver ≈2× the destratification effect at the same airflow.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Mountain, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, onPersistChange } from '@/lib/sessionPersistence';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

const BATHY_KEY = 'rb_bathymetry_v1';

interface BathyPoint { x: number; y: number; depth: number }
interface BathyState { points: BathyPoint[] }

export default function DeepZoneWeightingPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('deep_zone');
  const [bathy, setBathy] = useState<BathyState>(() => loadPersisted<BathyState>(BATHY_KEY, { points: [] }));

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === BATHY_KEY) setBathy(loadPersisted<BathyState>(BATHY_KEY, { points: [] }));
    });
    return () => unsub();
  }, []);

  const stats = useMemo(() => {
    const pts = bathy.points;
    if (!pts.length) return null;
    const depths = pts.map(p => p.depth).sort((a, b) => b - a);
    const n = depths.length;
    const p70_idx = Math.floor(n * 0.30);
    const deepThresh = depths[p70_idx] ?? depths[0];
    const meanDeep = depths.slice(0, p70_idx + 1).reduce((s, d) => s + d, 0) / Math.max(1, p70_idx + 1);
    const meanShallow = depths.slice(p70_idx + 1).reduce((s, d) => s + d, 0) / Math.max(1, n - p70_idx - 1);
    return { n, deepThresh, meanDeep, meanShallow };
  }, [bathy]);

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Mountain}
        title="Module — Deep-zone placement weighting"
        subtitle="Imteaz & Asaeda 2000: prioritise modules in deepest 30 % for 2× destratification efficiency"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${
              enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'
            }`}>
              <input type="checkbox" checked={enabled} onChange={() => toggle('deep_zone')} className="accent-emerald-500" />
              Module {enabled ? 'ENABLED' : 'DISABLED'}
            </label>
            <button onClick={() => navigate('/modules')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Modules
            </button>
          </div>
        }
      />

      <ModuleConflictBanner moduleId="deep_zone" />

      {stats ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400">Bathy points</div>
            <div className="text-2xl font-bold text-slate-100 mt-1">{stats.n.toLocaleString()}</div>
          </div>
          <div className="rounded-xl border-2 border-violet-700 bg-violet-900/15 p-4">
            <div className="text-xs uppercase tracking-wider text-violet-300">Deep-zone threshold</div>
            <div className="text-2xl font-bold text-violet-200 mt-1">{stats.deepThresh.toFixed(2)} m</div>
            <div className="text-xs text-slate-500 mt-1">deepest 30 % of points</div>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400">Mean deep-zone depth</div>
            <div className="text-2xl font-bold text-emerald-300 mt-1">{stats.meanDeep.toFixed(2)} m</div>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400">Mean shallow-zone depth</div>
            <div className="text-2xl font-bold text-slate-300 mt-1">{stats.meanShallow.toFixed(2)} m</div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-amber-700 bg-amber-900/15 p-4 text-sm text-amber-200">
          <strong>No bathymetric data loaded.</strong> Open the{' '}
          <button onClick={() => navigate('/bathymetry-import')} className="underline text-sky-300">Bathymetry Import</button>{' '}
          page and upload a survey CSV first. The deep-zone weighting needs measured depths.
        </div>
      )}

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> What this module does when enabled
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            <strong className="text-violet-300">Placement bias:</strong> Imteaz & Asaeda 2000 demonstrated that
            modules placed in the deepest 30 % of the reservoir footprint deliver up to <strong>2× the
            destratification effect</strong> of modules placed in shallower zones at the same total airflow.
            Plume buoyancy flux scales with rise height, so deeper modules generate more turbulent
            kinetic energy per Nm³ of air.
          </p>
          <p>
            <strong className="text-violet-300">Down-stream impact:</strong> When enabled, the recommendation
            engine on the Plume Coverage Map page applies a 1.6× efficiency multiplier to plume effectiveness
            at deep-zone positions and a 0.8× multiplier at shallow-zone positions. The "recommended modules
            for full coverage" figure drops by 15–25 % when at least half of modules can be placed in the
            deep zone.
          </p>
          <p>
            <strong className="text-violet-300">Cross-references:</strong> Gantzer, Bryant & Little (2009)
            showed 40 % better oxygen-utilisation efficiency from hypolimnion-coldest-layer placement vs
            mean-depth placement across two water-supply reservoirs.
          </p>
          <p className="italic text-slate-500">References: Imteaz & Asaeda 2000; Gantzer et al. 2009.</p>
        </div>
      </div>
    </div>
  );
}
